import { useCallback, useEffect, useState } from 'react';
import { httpClient, ROOT_URL } from '../../App';
import { BaseRecord, useNotification } from '@refinedev/core';
import { getErrorMessage } from '../../api/error';
import { ICompany } from '../../interfaces/companies';
import { IBankAccount } from '../../interfaces/bankAccounts';
import { IBank } from '../../interfaces/banks';

interface IDataEditBankAccount {
  bankAccountData: IBankAccount;
  bankData: IBank;
  companyData: ICompany;
}

export const useBankAccount = (fieldData?: BaseRecord) => {
  const [data, setData] = useState<IDataEditBankAccount>();
  const [isLoading, setIsLoading] = useState(false);
  const { open: openNotification } = useNotification();

  const fetch = useCallback(async () => {
    if (
      !Number.isInteger(fieldData?.id) ||
      !Number.isInteger(fieldData?.bankId) ||
      !Number.isInteger(fieldData?.companyId)
    ) {
      return;
    }
    setIsLoading(true);
    try {
      const accountResult = await httpClient.get(`${ROOT_URL}/api/v1/bank_accounts/${fieldData?.id}`);

      const bankResult = await httpClient.get(`${ROOT_URL}/api/v1/banks/${fieldData?.bankId}`);

      const companyResult = await httpClient.get(`${ROOT_URL}/api/v1/companies/${fieldData?.companyId}`);

      if (accountResult.status === 200 && bankResult.status === 200 && companyResult.status === 200) {
        const dataResult = {
          bankAccountData: accountResult.data,
          bankData: bankResult.data,
          companyData: companyResult.data,
        };
        setData(dataResult);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      setData(undefined);
      if (openNotification) {
        openNotification({
          key: 'edit_failure',
          message: `Error loading company #${fieldData?.id}`,
          description: message || 'Something went wrong',
          type: 'error',
        });
      }
    }
    setIsLoading(false);
  }, [fieldData?.id, fieldData?.bankId, fieldData?.companyId, openNotification]);

  const clearData = useCallback(() => {
    setData(undefined);
  }, []);

  useEffect(() => {
    if (fieldData === undefined) {
      clearData();
    } else {
      fetch();
    }
  }, [fieldData, clearData, fetch]);

  return {
    data,
    fetch,
    isLoading,
    clearData,
  };
};
